import { useEffect, useRef, useState } from 'react'
import { Box, Drawer, Group, Stack, Tabs } from '@mantine/core'
import { IconMessageCircle, IconSparkles } from '@tabler/icons-react'
import { useRefinementDockClickOutside } from '../hooks/useRefinementDockClickOutside'
import { REFINEMENT_SURFACE_Z_INDEX } from '../constants/refinement-layout'
import { RefinementRailPanel } from './RefinementRailPanel'
import { AiCustomizeSheet } from './AiCustomizeSheet'
import { CustomizeSheet } from './CustomizeSheet'
import type { AnonymousRefinementSheetOptions } from './CustomizeSheet'
import type { RefinementQuestion } from '../types'

type RefineLayout = 'drawer' | 'rail'

export interface RefinePlanSheetProps {
  opened: boolean
  onClose: () => void
  /** Desktop = rail, mobile = bottom dock (from parent + breakpoint). */
  layout: RefineLayout
  /** When the sheet opens, which tab to show first. */
  defaultSubTab: 'guided' | 'ai'
  hasGuidedQuestions: boolean
  questions: Array<RefinementQuestion>
  answers: Record<string, string>
  onAnswerChange: (key: string, value: string) => void
  anonymousRefinement?: AnonymousRefinementSheetOptions
  templateTitle: string
  templateSlug: string
  onAiSubmit: (prompt: string) => void
  /** Guided flow finished with all questions answered */
  onGuidedQuestionnaireCompleted?: (payload: {
    answerCount: number
    timeSpentSeconds: number
  }) => void
  /** User opens Ask AI (tab or AI-only template) */
  onAiSurfaceShown?: () => void
}

const dockPaddingBottom = 'calc(5.5rem + env(safe-area-inset-bottom, 0px))'

/**
 * Single refinement shell: guided Q&A and Ask AI in one place (Story 2.13).
 * Templates with no guided questions show AI only.
 * Mobile dock: no overlay (scrollable page); outside taps close via `useRefinementDockClickOutside`.
 */
export function RefinePlanSheet({
  opened,
  onClose,
  layout,
  defaultSubTab,
  hasGuidedQuestions,
  questions,
  answers,
  onAnswerChange,
  anonymousRefinement,
  templateTitle,
  templateSlug,
  onAiSubmit,
  onGuidedQuestionnaireCompleted,
  onAiSurfaceShown,
}: RefinePlanSheetProps) {
  const [tab, setTab] = useState<'guided' | 'ai'>(() =>
    hasGuidedQuestions ? defaultSubTab : 'ai'
  )
  const wasOpenRef = useRef(false)
  const aiSurfaceTrackedRef = useRef(false)

  useEffect(() => {
    if (!opened) {
      aiSurfaceTrackedRef.current = false
      return
    }
    if (!hasGuidedQuestions) {
      if (!aiSurfaceTrackedRef.current) {
        aiSurfaceTrackedRef.current = true
        onAiSurfaceShown?.()
      }
      return
    }
    if (tab === 'ai' && !aiSurfaceTrackedRef.current) {
      aiSurfaceTrackedRef.current = true
      onAiSurfaceShown?.()
    }
  }, [opened, hasGuidedQuestions, tab, onAiSurfaceShown])

  useEffect(() => {
    if (opened && !wasOpenRef.current) {
      setTab(hasGuidedQuestions ? defaultSubTab : 'ai')
    }
    wasOpenRef.current = opened
  }, [opened, defaultSubTab, hasGuidedQuestions])

  useEffect(() => {
    if (!hasGuidedQuestions && tab === 'guided') {
      setTab('ai')
    }
  }, [hasGuidedQuestions, tab])

  const showTabs = hasGuidedQuestions
  const guidedOpen = opened && (!showTabs || tab === 'guided')
  const aiOpen = opened && (!showTabs || tab === 'ai')

  const dockShellRef = useRefinementDockClickOutside(opened, onClose)

  const guidedBlock = (
    <CustomizeSheet
      isOpen={guidedOpen}
      onClose={onClose}
      questions={questions}
      answers={answers}
      onAnswerChange={onAnswerChange}
      anonymousRefinement={anonymousRefinement}
      layout="embedded"
      onGuidedQuestionnaireCompleted={onGuidedQuestionnaireCompleted}
    />
  )

  const aiBlock = (
    <AiCustomizeSheet
      opened={aiOpen}
      onClose={onClose}
      templateTitle={templateTitle}
      templateSlug={templateSlug}
      onSubmit={onAiSubmit}
      layout="embedded"
    />
  )

  const content = showTabs ? (
    <Tabs
      value={tab}
      onChange={(v) => {
        if (!v) return
        setTab(v as 'guided' | 'ai')
      }}
      keepMounted={false}
    >
      <Tabs.List grow mb="md">
        <Tabs.Tab
          value="guided"
          leftSection={
            <IconMessageCircle
              size={16}
              aria-hidden
              style={{ position: 'relative', top: 1 }}
            />
          }
        >
          Quick questions
        </Tabs.Tab>
        <Tabs.Tab
          value="ai"
          leftSection={
            <IconSparkles
              size={16}
              aria-hidden
              style={{ position: 'relative', top: 1 }}
            />
          }
        >
          Ask AI
        </Tabs.Tab>
      </Tabs.List>
      <Tabs.Panel value="guided" pt="xs">
        {guidedBlock}
      </Tabs.Panel>
      <Tabs.Panel value="ai" pt="xs">
        {aiBlock}
      </Tabs.Panel>
    </Tabs>
  ) : (
    <Stack gap="md">{aiBlock}</Stack>
  )

  if (layout === 'rail') {
    if (!opened) return null
    return (
      <RefinementRailPanel
        ariaLabel="Plan refinement"
        title="Refine plan"
        onClose={onClose}
        closeAriaLabel="Close plan refinement"
      >
        {content}
      </RefinementRailPanel>
    )
  }

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="bottom"
      size="auto"
      zIndex={REFINEMENT_SURFACE_Z_INDEX}
      title={null}
      withCloseButton={false}
      withOverlay={false}
      lockScroll={false}
      trapFocus={false}
      closeOnClickOutside={false}
      styles={{
        content: {
          borderTopLeftRadius: 'var(--mantine-radius-lg)',
          borderTopRightRadius: 'var(--mantine-radius-lg)',
          maxHeight: 'min(78dvh, 560px)',
          boxShadow:
            'light-dark(0 -0.25rem 1.25rem rgba(0, 0, 0, 0.12), 0 -0.25rem 1.25rem rgba(0, 0, 0, 0.45))',
        },
        body: {
          padding: 0,
        },
      }}
    >
      <Box ref={dockShellRef}>
        <Group justify="center" py="xs">
          <Box
            w={40}
            h={4}
            bg="gray.4"
            style={{ borderRadius: 'var(--mantine-radius-full)' }}
          />
        </Group>
        <Box
          px="md"
          pb="md"
          style={{
            paddingBottom: dockPaddingBottom,
          }}
        >
          {content}
        </Box>
      </Box>
    </Drawer>
  )
}
